import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  onSnapshot,
  doc,
  query,
  where,
  getDocs,
  writeBatch,
  runTransaction,
  Timestamp,
  increment,
} from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import Navigation from "../components/Navigation";
import SEO from "../components/SEO";
import PremiumAvantages from "../components/PremiumAvantages";
import HeroBgImg from "../assets/images/lyon.webp";

// ── Catalogue statique des parcours ──────────────────────────────────────────

const PARCOURS_COURTS = [
  {
    id: "vieux_lyon",
    num: "01",
    title: "Vieux Lyon & Berges de Saône",
    badge: "Jeux d'ombre et matières",
    description:
      "Ruelles pavées du Vieux Lyon, traboules de Saint-Jean, contre-jour entre les façades Renaissance, reflets sur la Saône. L'un des décors les plus denses de la ville pour travailler lumière et texture.",
    descriptionPremium:
      "Accompagnement personnalisé pendant 1 mois avec sélection commentée de tes images + −10 % sur ta prochaine réservation.",
    concepts: ["Contre-jour", "Ombres & lumières", "Textures de pierre", "Reflets sur l'eau"],
    prixStandard: 35,
    prixPremium: 55,
    duree: "2h",
    rdv: "Place Saint-Jean, devant la cathédrale Saint-Jean-Baptiste",
    difficulte: "Très faible",
    creneau: "8h–10h ou 17h–19h (printemps/été) · 9h–11h ou 15h–17h (automne/hiver)",
  },
  {
    id: "croix_rousse",
    num: "02",
    title: "Pentes de la Croix-Rousse",
    badge: "Street art & village",
    description:
      "Montée sur les pentes, fresques murales géantes, rues commerçantes animées, passages secrets entre les immeubles canuts. La vie de quartier la plus authentique de Lyon, idéale pour le street et les portraits volés.",
    descriptionPremium:
      "Accompagnement personnalisé pendant 1 mois — critique de tes images, suggestions de recadrage — + −10 % sur ta prochaine réservation.",
    concepts: ["Street art", "Portraits volés", "Textures urbaines", "Vie de quartier"],
    prixStandard: 35,
    prixPremium: 55,
    duree: "2h",
    rdv: "Place des Terreaux, devant l'hôtel de ville (côté fontaine Bartholdi)",
    difficulte: "Moyenne",
    creneau: "9h–11h ou 15h–17h",
  },
  {
    id: "gratte_ciel",
    num: "03",
    title: "Villeurbanne — Gratte-Ciel",
    badge: "Art déco",
    description:
      "Avenue Henri Barbusse, place Lazare Goujon, mairie de Villeurbanne. Symétrie parfaite, façades monumentales des années 30 et détails Art déco sur chaque bâtiment. Quartier plat, trottoirs larges — idéal grand angle.",
    descriptionPremium:
      "Accompagnement personnalisé pendant 1 mois avec retouche guidée d'un cliché d'architecture + −10 % sur ta prochaine réservation.",
    concepts: ["Symétrie", "Architecture Art déco", "Perspectives", "Grand angle"],
    prixStandard: 35,
    prixPremium: 55,
    duree: "2h",
    rdv: "Place Lazare Goujon, devant l'entrée de la mairie de Villeurbanne (côté tramway)",
    difficulte: "Très faible",
    creneau: "9h–11h ou 14h–16h · Mercredi ou samedi (marché actif)",
    note: "Les cours intérieures sont privées — nous photographions façades et espaces publics.",
  },
];

const PARCOURS_LONGS = [
  {
    id: "confluence",
    num: "04",
    title: "Presqu'île → Confluence",
    badge: "Contrastes photographiques",
    description:
      "De la place Bellecour jusqu'au musée des Confluences — 5 à 6 km d'architecture. Haussmannien vs. futuriste, reflets sur le Rhône, lignes de fuite, et l'œuvre spectaculaire de Zaha Hadid en point final.",
    descriptionPremium:
      "Accompagnement personnalisé pendant 1 mois pour construire ta séquence narrative + −10 % sur ta prochaine réservation.",
    concepts: ["Contrastes architecturaux", "Reflets", "Street photography", "Lignes de fuite"],
    prixStandard: 65,
    prixPremium: 95,
    duree: "4h",
    rdv: "Place Bellecour, près de la statue équestre de Louis XIV (côté Rhône)",
    difficulte: "Faible",
    creneau: "Début d'après-midi — finir avec la lumière dorée sur Confluence",
  },
  {
    id: "tete_dor",
    num: "05",
    title: "Tête d'Or & Berges du Rhône",
    badge: "Nature urbaine",
    description:
      "Serres tropicales, lac aux cygnes, roseraie, animaux du parc, puis berges du Rhône avec cyclistes et promeneurs. Macro floral, filé sur l'eau, portraits en lumière naturelle — le parc change de visage selon l'heure.",
    descriptionPremium:
      "Accompagnement personnalisé pendant 1 mois avec traitement RAW guidé d'un cliché végétal ou animalier + −10 % sur ta prochaine réservation.",
    concepts: ["Nature urbaine", "Macro", "Filé", "Portraits lumière naturelle"],
    prixStandard: 65,
    prixPremium: 95,
    duree: "4h",
    rdv: "Entrée principale du parc, porte des Enfants du Rhône (côté préfecture, pont Lafayette)",
    difficulte: "Très faible",
    creneau: "9h–13h (animaux plus actifs) ou 14h–18h",
  },
  {
    id: "fourviere",
    num: "06",
    title: "Les Deux Collines",
    badge: "Fourvière & Croix-Rousse",
    description:
      "Funiculaire jusqu'à la basilique Notre-Dame, panorama sur toute la ville, descente par le jardin du Rosaire, traversée des traboules du Vieux Lyon, puis montée à la Croix-Rousse par la Grande Côte. Le parcours le plus exigeant — et le plus généreux en images.",
    descriptionPremium:
      "Accompagnement personnalisé pendant 1 mois + −10 % sur ta prochaine réservation.",
    concepts: ["Panoramas", "Contre-jour", "Silhouettes", "Architecture religieuse"],
    prixStandard: 65,
    prixPremium: 95,
    duree: "4h",
    rdv: "Funiculaire Vieux-Lyon – Cathédrale Saint-Jean (station Vieux-Lyon métro D)",
    difficulte: "Élevée",
    creneau: "13h–17h (descente Fourvière + montée Croix-Rousse)",
  },
];

// ── Inclus ────────────────────────────────────────────────────────────────────

const INCLUS_STANDARD = [
  "Fiche technique du lieu (PDF)",
  "Carte annotée avec les meilleurs spots",
  "Check-list matériel adaptée au parcours",
  "Accès au serveur Discord de la communauté",
];

const INCLUS_PREMIUM = [
  "Tout le contenu Standard",
  "−10 % sur ta prochaine réservation (1 mois)",
  "Accompagnement personnalisé pendant 1 mois",
];

// ── Helpers date ──────────────────────────────────────────────────────────────

const formatDateFull = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatDateParts = (timestamp) => {
  if (!timestamp) return null;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return {
    day: date.getDate(),
    month: date.toLocaleDateString("fr-FR", { month: "long" }),
    year: date.getFullYear(),
  };
};

// Durée pendant laquelle une place est réservée en attendant le paiement Stripe.
// Passé ce délai, la place est automatiquement relâchée pour les prochains visiteurs.
const HOLD_MINUTES = 20;

// Libère les places dont le paiement n'a jamais été finalisé (utilisateur parti
// sans payer). Appelé juste avant de vérifier les places disponibles.
const releaseExpiredHolds = async (sessionId, sessionRef) => {
  const staleQuery = query(
    collection(db, "inscriptions"),
    where("type", "==", "parcours"),
    where("session_id", "==", sessionId),
    where("status", "==", "pending_payment"),
    where("held_until", "<=", Timestamp.now())
  );
  const staleSnap = await getDocs(staleQuery);
  if (staleSnap.empty) return;

  const batch = writeBatch(db);
  staleSnap.docs.forEach((d) => {
    batch.update(d.ref, { status: "expired" });
  });
  batch.update(sessionRef, { inscrits_count: increment(-staleSnap.size) });
  await batch.commit();
};

// ── Page ──────────────────────────────────────────────────────────────────────

function ParcoursLyonPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedTier, setSelectedTier] = useState("standard");
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", tel: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const firstFieldRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("inscription") === "confirmed") {
      setConfirmed(true);
      window.history.replaceState({}, "", "/parcours-lyon");
    }
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "parcours_sessions"),
      (snap) => {
        const data = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((s) => s.active !== false)
          .sort((a, b) => {
            const da = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
            const db_ = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
            return da - db_;
          });
        setSessions(data);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return unsub;
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setSelectedSession(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = selectedSession ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedSession]);

  useEffect(() => {
    if (selectedSession && firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, [selectedSession]);

  const openModal = (session, tier = "standard") => {
    setSelectedSession(session);
    setSelectedTier(tier);
    setForm({ prenom: "", nom: "", email: "", tel: "" });
    setFormError("");
    setSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.prenom.trim() || !form.nom.trim() || !form.email.trim()) {
      setFormError("Veuillez remplir tous les champs obligatoires.");
      return;
    }
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(form.email)) {
      setFormError("Adresse email invalide.");
      return;
    }

    setSubmitting(true);
    setFormError("");

    try {
      const sessionRef = doc(db, "parcours_sessions", selectedSession.id);
      let stripeLink = null;

      await releaseExpiredHolds(selectedSession.id, sessionRef);

      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(sessionRef);
        if (!snap.exists()) throw new Error("Session introuvable.");
        const data = snap.data();
        const count = data.inscrits_count || 0;
        const max = data.places_max || 6;
        if (count >= max) throw new Error("Ce parcours est complet.");

        const prix =
          selectedTier === "premium"
            ? data.prix_premium || 55
            : data.prix_standard || 35;

        stripeLink =
          selectedTier === "premium"
            ? data.stripe_link_premium
            : data.stripe_link_standard;

        // Document public (sans données perso) : sert au comptage des places
        // et au nettoyage des réservations expirées, lisible sans exposer
        // les coordonnées des inscrits.
        const inscRef = doc(collection(db, "inscriptions"));
        transaction.set(inscRef, {
          type: "parcours",
          session_id: selectedSession.id,
          session_title: selectedSession.title,
          session_date: selectedSession.date || null,
          parcours: selectedSession.parcours || null,
          tier: selectedTier,
          prix,
          created_at: Timestamp.now(),
          held_until: Timestamp.fromMillis(
            Date.now() + HOLD_MINUTES * 60 * 1000
          ),
          status: "pending_payment",
        });

        // Coordonnées de contact : dans une sous-collection séparée, jamais
        // lisible côté client (règles Firestore), consultable uniquement
        // depuis la console Firebase.
        transaction.set(doc(inscRef, "private", "contact"), {
          prenom: form.prenom.trim(),
          nom: form.nom.trim(),
          email: form.email.trim().toLowerCase(),
          tel: form.tel.trim(),
        });

        transaction.update(sessionRef, { inscrits_count: count + 1 });
      });

      if (stripeLink) {
        window.location.href = stripeLink;
      } else {
        setConfirmed(true);
        setSelectedSession(null);
      }
    } catch (err) {
      setFormError(err.message || "Une erreur est survenue. Veuillez réessayer.");
      setSubmitting(false);
    }
  };

  const renderParcoursList = (list, label) => (
    <div className="pl-group">
      <div className="pl-group__header">
        <span className="pl-group__label" aria-hidden="true">{label}</span>
        <span className="pl-group__rule" aria-hidden="true" />
      </div>
      <ol className="pl-list" aria-label={label}>
        {list.map((item) => (
          <li key={item.id} className="pl-row">
            <span className="pl-row__num" aria-hidden="true">{item.num}</span>
            <div className="pl-row__content">
              <div className="pl-row__header">
                <h3 className="pl-row__title">{item.title}</h3>
                <span className="pl-row__badge">{item.badge}</span>
              </div>
              {item.note && (
                <p className="pl-row__note">{item.note}</p>
              )}
              <p className="pl-row__desc">{item.description}</p>
              {(item.rdv || item.difficulte || item.creneau) && (
                <div className="pl-row__info">
                  {item.rdv && (
                    <span className="pl-row__info-item">
                      <strong>RDV</strong>{item.rdv}
                    </span>
                  )}
                  {item.difficulte && (
                    <span className="pl-row__info-item">
                      <strong>Difficulté</strong>{item.difficulte}
                    </span>
                  )}
                  {item.creneau && (
                    <span className="pl-row__info-item">
                      <strong>Créneau</strong>{item.creneau}
                    </span>
                  )}
                </div>
              )}
              {item.descriptionPremium && (
                <p className="pl-row__premium">
                  <span className="pl-row__premium-label">Premium — </span>
                  {item.descriptionPremium}
                </p>
              )}
              <ul className="pl-row__concepts" aria-label="Concepts abordés">
                {item.concepts.map((c, i) => (
                  <li key={i} className="pl-concept-chip">{c}</li>
                ))}
              </ul>
            </div>
            <div
              className="pl-row__prices"
              aria-label={`Tarif : ${item.prixStandard} euros standard, ${item.prixPremium} euros premium`}
            >
              <span className="pl-price pl-price--standard">
                <span className="pl-price__amount">{item.prixStandard} €</span>
                <span className="pl-price__label">standard</span>
              </span>
              <span className="pl-price__sep" aria-hidden="true">/</span>
              <span className="pl-price pl-price--premium">
                <span className="pl-price__amount">{item.prixPremium} €</span>
                <span className="pl-price__label">premium</span>
              </span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );

  return (
    <div className="pl-page">
      <SEO
        title="Parcours Photo Lyon"
        path="/parcours-lyon"
        description="Parcours photo guidés à Lyon : Vieux Lyon, Croix-Rousse, Confluence, Tête d'Or, Fourvière. Sorties 2h ou 4h. 6 pers max. Dès 35 €."
      />
      <Navigation />

      {/* ── Hero ── */}
      <section className="hero pl-hero">
        <div className="hero__bg" aria-hidden="true">
          <img
            src={HeroBgImg}
            alt=""
            loading="eager"
          />
        </div>
        <div className="hero__overlay" aria-hidden="true" />
        <div className="hero__content">
          <span className="pl-hero__label">Parcours photo · Lyon</span>
          <h1 className="pl-hero__title">
            Lyon comme<br />terrain de jeu.
          </h1>
          <p className="pl-hero__sub">
            6 parcours · 2h ou 4h · 6 pers max · Dès 35 €
          </p>
        </div>
      </section>

      {/* ── Catalogue ── */}
      <section className="pl-catalog">
        <div className="pl-catalog__container">
          <div className="pl-catalog__intro">
            <h2 className="pl-catalog__title">
              Explorez Lyon,<br />appareil en main.
            </h2>
            <p className="pl-catalog__body">
              Des quartiers choisis pour leur densité visuelle, des parcours
              pensés pour la progression. On marche, on observe, on shoote —
              chaque itinéraire est une leçon de photo à ciel ouvert.
            </p>
            <a href="#pl-sessions" className="pl-catalog__cta">
              Voir les prochaines dates
            </a>
          </div>

          {renderParcoursList(PARCOURS_COURTS, "Sorties courtes · 2 heures")}
          {renderParcoursList(PARCOURS_LONGS, "Demi-journées · 4 heures")}
        </div>
      </section>

      {/* ── Inclus ── */}
      <section className="pl-inclus">
        <div className="pl-inclus__container">
          <h2 className="pl-inclus__title">Inclus dans chaque parcours</h2>
          <div className="pl-inclus__grid">
            <div className="pl-inclus__col">
              <span className="pl-inclus__badge">Standard</span>
              <ul className="pl-inclus__list">
                {INCLUS_STANDARD.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="pl-inclus__col pl-inclus__col--premium">
              <span className="pl-inclus__badge pl-inclus__badge--premium">Premium</span>
              <ul className="pl-inclus__list">
                {INCLUS_PREMIUM.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <PremiumAvantages />

      {/* ── Prochaines sorties ── */}
      <section id="pl-sessions" className="pl-sessions">
        <div className="pl-sessions__container">
          <div className="pl-sessions__header">
            <h2 className="pl-sessions__title">Prochaines sorties</h2>
            <p className="pl-sessions__sub">
              Le point de rendez-vous précis est communiqué par email après inscription.
            </p>
          </div>

          {loading && (
            <p className="pl-sessions__state">Chargement des sessions…</p>
          )}

          {!loading && sessions.length === 0 && (
            <div className="pl-sessions__state pl-sessions__state--empty">
              <p>Aucune date programmée pour le moment.</p>
              <p>
                <a href="/contact">Contactez-moi</a> pour être prévenu en
                priorité à l'ouverture des prochaines inscriptions.
              </p>
            </div>
          )}

          <div className="pl-cards">
            {sessions.map((session) => {
              const placesLeft =
                (session.places_max || 6) - (session.inscrits_count || 0);
              const isFull = placesLeft <= 0;
              const dateParts = session.date
                ? formatDateParts(session.date)
                : null;

              return (
                <article
                  key={session.id}
                  className={`pl-card${isFull ? " pl-card--full" : ""}`}
                >
                  {dateParts && (
                    <div
                      className="pl-card__date"
                      aria-label={formatDateFull(session.date)}
                    >
                      <span className="pl-card__date-day">{dateParts.day}</span>
                      <span className="pl-card__date-month">
                        {dateParts.month}
                      </span>
                      <span className="pl-card__date-year">{dateParts.year}</span>
                    </div>
                  )}

                  <div className="pl-card__body">
                    <h3 className="pl-card__title">{session.title}</h3>
                    {session.parcours_label && (
                      <span className="pl-card__parcours-badge">
                        {session.parcours_label}
                      </span>
                    )}
                    {session.description && (
                      <p className="pl-card__desc">{session.description}</p>
                    )}
                    {session.description_premium && (
                      <p className="pl-card__desc pl-card__desc--premium">
                        <span className="pl-card__desc-label">Premium —</span>{" "}
                        {session.description_premium}
                      </p>
                    )}
                    <div className="pl-card__meta">
                      {session.duree && (
                        <span className="pl-card__meta-item">{session.duree}</span>
                      )}
                      {session.heure && (
                        <span className="pl-card__meta-item">{session.heure}</span>
                      )}
                      {session.lieu && (
                        <span className="pl-card__meta-item">{session.lieu}</span>
                      )}
                    </div>
                  </div>

                  <div className="pl-card__footer">
                    <span
                      className={`pl-card__spots ${
                        isFull
                          ? "pl-card__spots--full"
                          : "pl-card__spots--ok"
                      }`}
                    >
                      {isFull
                        ? "Complet"
                        : `${placesLeft} place${placesLeft > 1 ? "s" : ""} restante${placesLeft > 1 ? "s" : ""}`}
                    </span>

                    {!isFull ? (
                      <div className="pl-card__ctas">
                        <button
                          className="pl-card__cta pl-card__cta--standard"
                          onClick={() => openModal(session, "standard")}
                        >
                          {session.prix_standard ?? 35} € — Standard
                        </button>
                        <button
                          className="pl-card__cta pl-card__cta--premium"
                          onClick={() => openModal(session, "premium")}
                        >
                          {session.prix_premium ?? 55} € — Premium
                        </button>
                      </div>
                    ) : (
                      // Une place peut s'être libérée (paiement jamais finalisé) sans
                      // que le compteur affiché soit encore à jour — la transaction
                      // vérifie la vraie disponibilité au moment de l'envoi.
                      <button
                        className="pl-card__cta pl-card__cta--waitlist"
                        onClick={() => openModal(session, "standard")}
                      >
                        Tenter ma chance
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Toast confirmation ── */}
      {confirmed && (
        <div className="pl-toast" role="status">
          <div className="pl-toast__inner">
            <div>
              <strong>Inscription enregistrée !</strong>
              <p>
                Votre place est réservée. Un email de confirmation Stripe vous
                sera envoyé après le paiement.
              </p>
              <p>
                Pensez à prévoir votre appareil photo (reflex ou hybride),
                une carte SD et une batterie chargée.
              </p>
            </div>
            <button
              className="pl-toast__close"
              onClick={() => setConfirmed(false)}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* ── Modal inscription ── */}
      {selectedSession && (
        <div
          className="pl-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pl-modal-heading"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedSession(null);
          }}
        >
          <div className="pl-modal__panel">
            <button
              className="pl-modal__close"
              onClick={() => setSelectedSession(null)}
              aria-label="Fermer"
            >
              ×
            </button>

            <div className="pl-modal__session-info">
              <div className="pl-modal__session-top">
                <span className="pl-modal__session-name">
                  {selectedSession.title}
                </span>
                <span
                  className={`pl-modal__tier-badge${
                    selectedTier === "premium"
                      ? " pl-modal__tier-badge--premium"
                      : ""
                  }`}
                >
                  {selectedTier === "premium" ? "Premium" : "Standard"}
                </span>
              </div>
              {selectedSession.date && (
                <span className="pl-modal__session-date">
                  {formatDateFull(selectedSession.date)}
                </span>
              )}
            </div>

            <form
              className="pl-modal__form"
              onSubmit={handleSubmit}
              noValidate
            >
              <h2 id="pl-modal-heading" className="pl-modal__form-title">
                Vos informations
              </h2>

              <div className="pl-modal__row">
                <label className="pl-modal__field">
                  <span className="pl-modal__label">Prénom *</span>
                  <input
                    ref={firstFieldRef}
                    type="text"
                    value={form.prenom}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, prenom: e.target.value }))
                    }
                    autoComplete="given-name"
                    required
                    disabled={submitting}
                  />
                </label>
                <label className="pl-modal__field">
                  <span className="pl-modal__label">Nom *</span>
                  <input
                    type="text"
                    value={form.nom}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, nom: e.target.value }))
                    }
                    autoComplete="family-name"
                    required
                    disabled={submitting}
                  />
                </label>
              </div>

              <label className="pl-modal__field">
                <span className="pl-modal__label">Email *</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                  autoComplete="email"
                  required
                  disabled={submitting}
                />
              </label>

              <label className="pl-modal__field">
                <span className="pl-modal__label">Téléphone</span>
                <input
                  type="tel"
                  value={form.tel}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, tel: e.target.value }))
                  }
                  autoComplete="tel"
                  disabled={submitting}
                />
              </label>

              {formError && (
                <p className="pl-modal__error" role="alert">
                  {formError}
                </p>
              )}

              <button
                type="submit"
                className="pl-modal__submit"
                disabled={submitting}
              >
                {submitting
                  ? "Traitement…"
                  : `Procéder au paiement — ${
                      selectedTier === "premium"
                        ? (selectedSession.prix_premium ?? 55)
                        : (selectedSession.prix_standard ?? 35)
                    } €`}
              </button>

              <p className="pl-modal__note">
                Paiement sécurisé via Stripe. Votre place est réservée
                pendant {HOLD_MINUTES} minutes le temps de finaliser le
                paiement.
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParcoursLyonPage;
